import { createElement, type ReactNode } from "react";

import type { ScoutDataChangeSummary } from "../hooks/types";
import type { ScoutConversationPrivateDocumentRow } from "../hooks/useScoutConversationPrivateDocuments";
import type { ScoutToolPart } from "./message/scoutChatMessage";

import {
  DATA_CHANGE_PROPOSAL_KIND,
  isAskApplyExecuteGraphqlTool,
  isScoutDataChangeProposalTool,
  parseScoutDataChangeProposalFromSummary,
  parseScoutDataChangeSummariesFromToolOutput,
  respondedStatusForDataChange,
  type ScoutDataChangeProposal,
} from "./scoutDataChangeProposal";
import { ScoutDataChangeProposalStack } from "./ScoutDataChangeProposalStack";
import {
  LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND,
  mapAwaitingConfirmationDocumentsToLargeFileProposal,
  type ScoutLargeFileFullAnalysisProposal,
} from "./scoutLargeFileFullAnalysisProposal";
import {
  parseScoutTenantDocumentProposal,
  PROPOSE_TENANT_DOCUMENTS_TOOL_NAME,
  TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND,
} from "./scoutTenantDocumentProposal";
import { ScoutTenantDocumentProposalCard } from "./ScoutTenantDocumentProposalCard";
import { defineScoutToolCard, renderScoutToolCards, type ScoutToolCard } from "./scoutToolCard";

/**
 * Proposal kinds that Oak can resolve via the shared HITL protocol.
 * Matches `ScoutUserConfirmationProposalKind` in encamp-scout/core; Oak
 * keeps its own copy so the UI package does not depend on scout-core.
 */
export type ScoutProposalKind =
  | typeof TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND
  | typeof LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND
  | typeof DATA_CHANGE_PROPOSAL_KIND;

/**
 * Tool-result discovery entry: the kind's card, rendered from assistant tool
 * parts in the message-end HITL cluster.
 */
export type ScoutToolResultProposalRegistryEntry = {
  source: "tool_result";
  kind: ScoutProposalKind;
  card: ScoutToolCard;
};

/**
 * Domain-status discovery entry: derive pending proposals from durable
 * domain state (e.g. `PrivateDocument.extractionStatus`), not tool output.
 * UI surface is kind-specific — large-file keeps composer-chrome buttons
 * rather than an inline card.
 */
export type ScoutDomainStatusProposalRegistryEntry = {
  source: "domain_status";
  kind: ScoutProposalKind;
  /**
   * Map conversation domain rows into a proposal view model, or null when
   * nothing is awaiting confirmation for this kind.
   */
  discoverFromDocuments: (
    documents: ReadonlyArray<ScoutConversationPrivateDocumentRow>
  ) => ScoutLargeFileFullAnalysisProposal | null;
};

/**
 * Kind → card + tool-result discovery. Confirm/decline mutations live in
 * `ScoutProposalContext` (runtime-bound); this table owns the static UI +
 * parse side. Add a row when wiring a new tool-emitted approval kind.
 *
 * Data-change cards are collected from execute_graphql `dataChanges` and
 * rendered via {@link ScoutDataChangeProposalStack} so multi-mutation turns
 * share Accept all / Reject all chrome (see
 * {@link renderScoutProposalCardsForMessage}).
 */
export const SCOUT_TOOL_RESULT_PROPOSAL_REGISTRY = [
  {
    source: "tool_result",
    kind: TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND,
    card: defineScoutToolCard({
      toolName: PROPOSE_TENANT_DOCUMENTS_TOOL_NAME,
      hideTraceRow: false,
      parse: parseScoutTenantDocumentProposal,
      render: (proposal) => createElement(ScoutTenantDocumentProposalCard, { proposal }),
    }),
  },
] as const satisfies ReadonlyArray<ScoutToolResultProposalRegistryEntry>;

/**
 * Kind → domain-status discovery. No Card — each kind owns its surface
 * (large-file: `ScoutLargeDocumentOptInButtons` in ChatFooter). Add a row
 * when wiring a new DB/status-driven approval kind.
 *
 * To add a kind: (1) shared proposal shape in encamp-scout/core, (2) Oak
 * mapper + synthetic-event helpers, (3) handlers in ScoutProposalContext,
 * (4) registry row here, (5) wire discover + resolve at the host surface.
 */
export const SCOUT_DOMAIN_STATUS_PROPOSAL_REGISTRY = [
  {
    source: "domain_status",
    kind: LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND,
    discoverFromDocuments: mapAwaitingConfirmationDocumentsToLargeFileProposal,
  },
] as const satisfies ReadonlyArray<ScoutDomainStatusProposalRegistryEntry>;

/**
 * Collect parsed data-change cards from execute_graphql tool parts.
 *
 * Cards come from `toolOutput.dataChanges` on propose / Auto calls.
 * Ask-apply (`approvedProposalId`) is skipped even when the payload still
 * includes `dataChanges`. `{ success, data }` with no `dataChanges` is not
 * a card.
 */
export function collectScoutDataChangeProposalsFromToolParts(
  tools: ReadonlyArray<ScoutToolPart>
): ScoutDataChangeProposal[] {
  const proposals: ScoutDataChangeProposal[] = [];
  for (const toolPart of tools) {
    if (!isScoutDataChangeProposalTool(toolPart)) continue;
    if (isAskApplyExecuteGraphqlTool(toolPart)) continue;
    for (const change of parseScoutDataChangeSummariesFromToolOutput(toolPart.toolOutput)) {
      const proposal = parseScoutDataChangeProposalFromSummary(change);
      if (proposal) {
        proposals.push(proposal);
      }
    }
  }
  return proposals;
}

/**
 * Cards live on the message that made the tool call. Overlay patches
 * status / undo by id only — it does not create cards or use turnId.
 */
export function collectScoutDataChangeProposals(input: {
  tools: ReadonlyArray<ScoutToolPart>;
  dataChanges?: ReadonlyArray<ScoutDataChangeSummary>;
}): ScoutDataChangeProposal[] {
  const overlayById = new Map((input.dataChanges ?? []).map((change) => [change.id, change]));
  const proposals: ScoutDataChangeProposal[] = [];
  const seen = new Set<string>();
  for (const proposal of collectScoutDataChangeProposalsFromToolParts(input.tools)) {
    if (seen.has(proposal.proposalId)) continue;
    seen.add(proposal.proposalId);
    proposals.push(applyOverlayStatus(proposal, overlayById.get(proposal.proposalId)));
  }
  return proposals;
}

function applyOverlayStatus(
  proposal: ScoutDataChangeProposal,
  overlay: ScoutDataChangeSummary | undefined
): ScoutDataChangeProposal {
  if (!overlay) return proposal;
  const rowStatus = respondedStatusForDataChange(overlay.status);
  if (rowStatus === proposal.rowStatus) return proposal;
  return { ...proposal, rowStatus };
}

/**
 * Render all tool-result HITL approval cards for an assistant message.
 * Call once at the end of the message (after text/tool/todo blocks), not
 * per tool group. Data-change proposals share one stack so Accept all /
 * Reject all covers the whole reply; other registry kinds render one card
 * each after the stack.
 */
export function renderScoutProposalCardsForMessage(
  tools: ScoutToolPart[],
  options?: {
    dataChanges?: ReadonlyArray<ScoutDataChangeSummary>;
  }
): ReactNode[] {
  const cards: ReactNode[] = [];
  const dataChangeProposals = collectScoutDataChangeProposals({
    tools,
    dataChanges: options?.dataChanges,
  });

  if (dataChangeProposals.length > 0) {
    cards.push(
      createElement(ScoutDataChangeProposalStack, {
        key: "data-change-proposal-stack",
        proposals: dataChangeProposals,
      })
    );
  }

  cards.push(
    ...renderScoutToolCards(
      tools.filter((toolPart) => !isScoutDataChangeProposalTool(toolPart)),
      SCOUT_TOOL_RESULT_PROPOSAL_REGISTRY.map((entry) => entry.card)
    )
  );

  return cards;
}

/**
 * Discover the pending large-file full-analysis batch from conversation
 * private-document rows (domain-status source).
 */
export function discoverLargeFileFullAnalysisProposal(
  documents: ReadonlyArray<ScoutConversationPrivateDocumentRow>
): ScoutLargeFileFullAnalysisProposal | null {
  const entry = SCOUT_DOMAIN_STATUS_PROPOSAL_REGISTRY.find(
    (candidate) => candidate.kind === LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND
  );
  return entry?.discoverFromDocuments(documents) ?? null;
}
